import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Button, Card, CardContent, Stack, type Theme, Typography, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import {
  CatalogDTO,
  CatalogItemDetailsDTO,
  getGetCatalogItemDetailsQueryKey,
  PlatformLocale,
  Refinement,
  useAddCatalogItemToProcess
} from '@/api/generated';
import CheckIcon from '@/assets/icons/check.svg?react';
import RefinementModal from '@/components/RefinementModal/RefinementModal';
import RichTextLabel from '@/components/RichTextLabel/RichTextLabel';
import TestInformationModal from '@/components/TestInformationModal/TestInformationModal';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

interface CatalogItemCardProps {
  catalogItem: CatalogDTO;
  isSelected?: boolean;
  onOrder?: (id: string) => void;
  processId: string;
  currentLocale: PlatformLocale;
  onAddSuccess?: () => void;
  readonly?: boolean;
}

const CatalogItemCard: FC<CatalogItemCardProps> = ({
  catalogItem,
  isSelected,
  onOrder,
  processId,
  currentLocale,
  onAddSuccess,
  readonly
}) => {
  const { t, i18n } = useTranslation(['settings', 'processes']);
  const { spacing } = useTheme();
  const queryClient = useQueryClient();
  const [currentRefinement, setCurrentRefinement] = useState<Refinement | null>(null);
  const [refinementList, setRefinementList] = useState<Refinement[]>([]);
  const [isTestInfoModalOpen, setIsTestInfoModalOpen] = useState(false);
  const { mutateAsync: addCatalogItemToProcess } = useAddCatalogItemToProcess();
  const isAvailable = catalogItem?.isAvailable ?? true;

  const handleAdd = useCallback(async () => {
    try {
      const response = await addCatalogItemToProcess({
        processId,
        data: {
          catalogId: catalogItem.catalogId!,
          locale: currentLocale,
          refinements: refinementList
        }
      });
      if (response.refinement) {
        setCurrentRefinement(response.refinement);
      } else {
        onAddSuccess?.();
        setRefinementList([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Error adding test definition: ${e}`);
    }
  }, [addCatalogItemToProcess, processId, catalogItem.catalogId, currentLocale, refinementList, onAddSuccess]);

  const handleRefinementSubmit = useCallback(
    async (selectedOptions: string[]) => {
      if (currentRefinement) {
        const updatedRefinements = [...refinementList, { id: currentRefinement.id, values: selectedOptions }];
        setRefinementList(updatedRefinements);
        try {
          const response = await addCatalogItemToProcess({
            processId,
            data: {
              catalogId: catalogItem.catalogId!,
              locale: currentLocale,
              refinements: updatedRefinements
            }
          });
          if (response.refinement) {
            setCurrentRefinement(response.refinement);
          } else {
            onAddSuccess?.();
            setRefinementList([]);
            setCurrentRefinement(null);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`Error adding test definition with refinements: ${e}`);
        }
      }
    },
    [
      currentRefinement,
      refinementList,
      addCatalogItemToProcess,
      processId,
      catalogItem.catalogId,
      currentLocale,
      onAddSuccess
    ]
  );

  const handleCardClick = useCallback(() => {
    const queryKey = getGetCatalogItemDetailsQueryKey(catalogItem.catalogId!, {
      locale: i18n.language as PlatformLocale
    });

    queryClient.setQueryData<CatalogItemDetailsDTO>(queryKey, prev => {
      if (prev) return prev;

      return {
        catalogId: catalogItem.catalogId!,
        name: catalogItem.name!,
        description: catalogItem.description,
        iconUrl: catalogItem.iconUrl,
        estimatedDuration: catalogItem.estimatedDuration,
        itemCount: catalogItem.itemCount,
        isAvailable: catalogItem.isAvailable ?? true,
        supportedLocales: (catalogItem.supportedLocales as PlatformLocale[]) ?? [],
        infoGrid: catalogItem.infoGrid ?? [],
        category: catalogItem.category!,
        locale: i18n.language as PlatformLocale,
        assessmentType: catalogItem.assessmentType as CatalogItemDetailsDTO['assessmentType']
      };
    });

    setIsTestInfoModalOpen(true);
  }, [catalogItem, i18n.language, queryClient]);

  const handleCloseInfoModal = useCallback(() => {
    setIsTestInfoModalOpen(false);
  }, []);

  const handleButtonClick = useCallback(() => {
    switch (true) {
      case readonly: {
        break;
      }
      case !isAvailable: {
        onOrder?.(catalogItem.catalogId!);
        break;
      }
      case !isSelected: {
        handleAdd();
        break;
      }
    }
  }, [readonly, isAvailable, isSelected, onOrder, catalogItem.catalogId, handleAdd]);

  return (
    <>
      <Card
        elevation={0}
        onClick={handleCardClick}
        sx={{
          borderRadius: spacing(0.5),
          border: borders.default,
          borderColor: isSelected ? 'primary.main' : 'grey.400',
          bgcolor: 'background.default',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <CardContent component={Stack} sx={{ position: 'relative', pt: 6, height: '100%', textAlign: 'start' }}>
          <Stack
            width="100%"
            height={40}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            bgcolor="grey.100"
            p={spacing(1, 2)}
            position="absolute"
            left={0}
            top={0}
          >
            <Typography variant="body2" sx={{ color: 'grey.800' }}>
              {t(`processes:layouts.processes.details.info.processTypes.${catalogItem?.assessmentType?.toLowerCase()}`)}
            </Typography>
          </Stack>
          <Stack gap={1} direction="row" alignItems="center" mb={1} sx={{ svg: { flexShrink: 0 } }}>
            <Box width={48} height={48} component="img" src={catalogItem.iconUrl} />
            {/* <Typography variant="body2">{categoryName}</Typography> */}
          </Stack>
          <Stack alignItems="start">
            <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
              {catalogItem.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                maxHeight: '1.5rem',
                whiteSpace: 'pre',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              }}
            >
              <RichTextLabel html={catalogItem.description || ''} />
            </Typography>
          </Stack>

          <Stack direction="row" gap={0.5} mt={0.5} mb={2} sx={{ '& .MuiTypography-root': { color: 'grey.600' } }}>
            {catalogItem.itemCount && (
              <Typography variant="body3">
                {t('processSettings.form.card.items', { count: catalogItem.itemCount })}
              </Typography>
            )}
            {catalogItem.itemCount && catalogItem.estimatedDuration && <Typography variant="body3">&bull;</Typography>}
            {catalogItem.estimatedDuration && (
              <Typography variant="body3">
                {t('processSettings.form.card.estimatedDuration', { minutes: catalogItem.estimatedDuration })}
              </Typography>
            )}
          </Stack>

          <Stack flexGrow={1} />
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Stack flexGrow={1} />
            {!readonly && (
              <Button
                variant={isSelected || !isAvailable ? 'outlined' : 'contained'}
                color="primary"
                fullWidth
                sx={mergeSx<Theme>(
                  {
                    p: spacing(0.75, 1.25),
                    flexGrow: 0,
                    width: 'auto',
                    minWidth: 'auto'
                  },
                  isSelected && {
                    border: borders.half,
                    borderColor: 'grey.400',
                    '& .MuiButton-startIcon': {
                      marginInlineEnd: 1,
                      marginInlineStart: -0.5
                    }
                  }
                )}
                onClick={e => {
                  e.stopPropagation();
                  handleButtonClick();
                }}
                startIcon={isSelected && <CheckIcon />}
              >
                <Typography variant="body3" sx={{ lineHeight: 1.33, fontWeight: typography.fontWeightMedium }}>
                  {!isAvailable && t('processSettings.form.card.orderButton')}
                  {isAvailable && !isSelected && t('processSettings.form.card.addButton')}
                  {isAvailable && isSelected && t('processSettings.form.card.addedButton')}
                </Typography>
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
      {currentRefinement && (
        <RefinementModal
          refinement={currentRefinement}
          catalogItemName={catalogItem.name!}
          onSubmit={handleRefinementSubmit}
          onClose={() => {
            setCurrentRefinement(null);
            setRefinementList([]);
          }}
        />
      )}
      {isTestInfoModalOpen && (
        <TestInformationModal
          onClose={handleCloseInfoModal}
          catalogItemId={catalogItem.catalogId!}
          onButtonClick={handleButtonClick}
          isSelected={isSelected}
          isAvailable={isAvailable}
          readonly={readonly}
        />
      )}
    </>
  );
};

export default CatalogItemCard;
